import { Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import { useAskHelenaMessageFeedback } from "../hooks/useAskHelenaMessageFeedback";
import { useAskHelenaTurnResume } from "../hooks/useAskHelenaTurnResume";
import { useChatAutoScroll } from "../hooks/useChatAutoScroll";
import {
  askHelenaError,
  askHelenaMessages,
  askHelenaStatus,
  isAskHelenaBusy,
} from "../store";
import { AskHelenaConfirmCard } from "./AskHelenaConfirmCard";
import { AskHelenaErrorBanner } from "./AskHelenaErrorBanner";
import {
  ASK_HELENA_MESSAGE_ACTIONS_CLASS,
  AskHelenaMessageActions,
} from "./AskHelenaMessageActions";
import { AskHelenaMessageBubble } from "./AskHelenaMessageBubble";
import { AskHelenaStatusRow } from "./AskHelenaStatusRow";

interface AskHelenaThreadProps {
  onApprove: (pendingWriteId: string) => void;
  onDecline: (pendingWriteId: string) => void;
}

export const AskHelenaThread = ({
  onApprove,
  onDecline,
}: AskHelenaThreadProps) => {
  const messages = useAtomValue(askHelenaMessages);
  const status = useAtomValue(askHelenaStatus);
  const error = useAtomValue(askHelenaError);
  const isBusy = useAtomValue(isAskHelenaBusy);
  const rateMessage = useAskHelenaMessageFeedback();
  const { isResuming, resume } = useAskHelenaTurnResume();

  const lastMessage = messages[messages.length - 1];

  const { scrollRef, handleScroll } = useChatAutoScroll({
    // The id is part of the key so swapping to a history thread of the same
    // length still counts as new messages arriving.
    jumpKey: `${messages.length}:${lastMessage?.id ?? ""}`,
    // Status is a follow trigger too: it adds and removes the indicator row
    // below the answer, which would otherwise sit just out of view. The
    // approval card is the same case — it appears below the answer and is the
    // one thing in the thread the user must actually see.
    followKey: `${lastMessage?.content.length ?? 0}:${status}:${error ?? ""}:${
      lastMessage?.pendingWrite?.pendingWriteId ?? ""
    }`,
  });

  return (
    <Stack
      ref={scrollRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        gap: "24px",
        p: "8px 0 24px",
        overflowY: "auto",
      }}
    >
      {messages.map((message, index) => {
        const pendingWrite = message.pendingWrite;
        const isLast = index === messages.length - 1;
        const isUser = message.role === "user";
        const showActions =
          message.role === "assistant" &&
          Boolean(message.content) &&
          !pendingWrite &&
          !(isLast && isBusy);

        const confirmCard = pendingWrite ? (
          <AskHelenaConfirmCard
            pendingWrite={pendingWrite}
            isResolved={message.isPendingWriteResolved}
            onApprove={() => onApprove(pendingWrite.pendingWriteId)}
            onDecline={() => onDecline(pendingWrite.pendingWriteId)}
          />
        ) : null;

        return (
          // No alignItems on this wrapper: the bubble sets its own alignSelf,
          // and a user message must stay right-aligned.
          <Stack
            key={message.id}
            sx={{
              gap: "12px",
              [`&:hover .${ASK_HELENA_MESSAGE_ACTIONS_CLASS}, &:focus-within .${ASK_HELENA_MESSAGE_ACTIONS_CLASS}`]:
                { opacity: 1, pointerEvents: "auto" },
            }}
          >
            {/* On an assistant message the card comes FIRST, above the bubble.
                A suspended turn has no answer text yet, and once it resumes the
                tokens append to this same message — so anything the bubble
                holds is the RESULT of the proposal and has to read below it.

                A card restored from history hangs off the USER message instead
                (a suspended turn writes no assistant message, so the service
                has nothing else to attach it to) and there it belongs AFTER the
                bubble: below the question that asked for the change. */}
            {isUser ? null : confirmCard}
            <AskHelenaMessageBubble
              message={message}
              onResume={message.isInterrupted ? resume : null}
              isResuming={message.isInterrupted && isResuming}
            />
            {isUser ? confirmCard : null}

            {showActions ? (
              <AskHelenaMessageActions
                content={message.content}
                messageId={message.serverMessageId}
                feedback={message.feedback}
                onRate={rateMessage}
                showRegenerate={isLast}
                isAlwaysVisible={isLast}
              />
            ) : null}
          </Stack>
        );
      })}

      <AskHelenaStatusRow />

      {error && <AskHelenaErrorBanner message={error} />}
    </Stack>
  );
};
